import React from 'react'
import styled from 'styled-components'

import { useTranslation } from '@root/shared/localization'

import type { PaletteVariant } from '@blade/ui-kit'
import type { ChildrenProps } from '@root/shared/react'

interface ProfileRankTagsProps {
  trading: {
    count: number | string
    onClick?: () => void
  }
  referral: {
    count: number | string
    onClick?: () => void
  }
}

export const ProfileRankTags = (props: ProfileRankTagsProps) => {
  const { t } = useTranslation()

  return (
    <ProfileRankTagsRoot>
      <Rank
        color={'primary'}
        label={t('trading')}
        count={props.trading.count}
        onClick={props.trading.onClick}
      />
      <Rank
        color={'green'}
        label={t('referral')}
        count={props.referral.count}
        onClick={props.referral.onClick}
      />
    </ProfileRankTagsRoot>
  )
}
const ProfileRankTagsRoot = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`

type RankProps = {
  count: number | string
  color: PaletteVariant
  label: string
  onClick?: () => void
}
const Rank = (props: RankProps) => {
  return (
    <RankRoot
      $onClick={props.onClick}
      type="button"
      onClick={props.onClick}
      aria-disabled={!props.onClick}
    >
      <RankTag color={props.color}>{props.count}</RankTag>
      <RankLabel>{props.label}</RankLabel>
    </RankRoot>
  )
}

const RankLabel = styled.div`
  font-weight: 600;
  font-size: 10px;
  text-transform: uppercase;
  padding: 4px 8px 4px 5px;
`

const RankRoot = styled.button<{ $onClick: undefined | (() => void) }>`
  all: unset;
  cursor: ${(x) => (x.$onClick ? 'pointer' : 'auto')};
  box-sizing: border-box;

  border-radius: 64px;
  display: flex;
  align-items: center;
  background: var(--backgroundA-3);

  transition: background 0.2s ease-in-out;

  &[aria-disabled='false'] {
    &:hover {
      background: var(--backgroundA-4);
    }
  }
`

export type RankTagProps = {
  color: PaletteVariant
} & ChildrenProps

export const RankTag: React.FC<RankTagProps> = ({
  children,
  color = 'blue',
  ...props
}: RankTagProps) => {
  return (
    <Root $highlightColor={color} {...props}>
      <span>{children}</span>
    </Root>
  )
}

const Root = styled.div<{
  $highlightColor: PaletteVariant
}>`
  border-radius: 64px;

  height: 24px;
  min-width: 32px;

  display: flex;
  align-items: center;
  justify-content: center;
  padding-inline: 8px;

  color: var(--text-12);
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  line-height: normal;
  background: ${(props) =>
    props.$highlightColor === 'green'
      ? 'var(--upAccent-9)'
      : 'var(--primary-9)'};
`
